import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from './toast.service';

export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'DUPLICATE_RESOURCE'
  | 'BUSINESS_RULE_VIOLATION'
  | 'INTERNAL_ERROR'
  | string;

export interface ApiErrorResponse {
  code: ApiErrorCode;
  errors: string[];
}

export interface ApiErrorHandlers {
  onValidationErrors?: (messages: string[]) => void;
  onBusinessRuleErrors?: (messages: string[]) => void;
  onNotFound?: (message: string, messages: string[]) => void;
  onUnauthorized?: () => void;
  onForbidden?: (message: string, messages: string[]) => void;
  onDuplicateResource?: (messages: string[]) => void;
  onDefault?: (message: string, messages: string[]) => void;
}

@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private router = inject(Router);
  private toast = inject(ToastService);

  normalize(error: unknown): ApiErrorResponse {
    const fallbackMessage = 'Ha ocurrido un error inesperado. Intenta de nuevo.';
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      error.error &&
      typeof error.error === 'object'
    ) {
      const body = (error as any).error;
      if (body?.code && Array.isArray(body?.errors)) {
        return { code: body.code, errors: body.errors.length ? body.errors : [fallbackMessage] };
      }
    }

    const message =
      (error as any)?.message ??
      (error as any)?.error?.message ??
      fallbackMessage;

    return {
      code: ((error as any)?.error?.code as ApiErrorCode) || 'INTERNAL_ERROR',
      errors: [message],
    };
  }

  handle(error: unknown, handlers: ApiErrorHandlers = {}): ApiErrorResponse {
    const apiError = this.normalize(error);
    const primary = apiError.errors[0] ?? 'Ha ocurrido un error inesperado.';

    switch (apiError.code) {
      case 'VALIDATION_ERROR':
        if (handlers.onValidationErrors) handlers.onValidationErrors(apiError.errors);
        else this.toast.show(apiError.errors.join('<br>'), 'warning', 6000);
        break;

      case 'NOT_FOUND':
        if (handlers.onNotFound) handlers.onNotFound(primary, apiError.errors);
        else this.toast.show(primary, 'warning');
        break;

      case 'UNAUTHORIZED':
        if (handlers.onUnauthorized) handlers.onUnauthorized();
        else {
          this.toast.show(primary, 'warning');
          this.router.navigate(['/auth/signin']);
        }
        break;

      case 'FORBIDDEN':
        if (handlers.onForbidden) handlers.onForbidden(primary, apiError.errors);
        else this.toast.show(primary, 'warning');
        break;

      case 'DUPLICATE_RESOURCE':
        if (handlers.onDuplicateResource) handlers.onDuplicateResource(apiError.errors);
        else this.toast.show(apiError.errors.join('<br>'), 'warning', 6000);
        break;

      case 'BUSINESS_RULE_VIOLATION':
        if (handlers.onBusinessRuleErrors) handlers.onBusinessRuleErrors(apiError.errors);
        else this.toast.show(apiError.errors.join('<br>'), 'warning', 6000);
        break;

      default:
        if (handlers.onDefault) handlers.onDefault(primary, apiError.errors);
        else this.toast.show(primary, 'warning');
        break;
    }

    return apiError;
  }
}
